<template>
  <v-container fluid class="pa-0 ma-0">
    <v-card expand flat class=" full-card-desktop py-0 px-0"   >
      <v-tabs-items v-model="activeTab">
        <v-tab-item value="connect" lazy>

          <v-toolbar flat color="white" class="darken-1">
            <v-icon color="red" v-if="!hasCurrentCredential">
              report
            </v-icon>
            <v-icon :color="xBlue" v-else>offline_bolt</v-icon>
            <v-toolbar-title>Xero Direct</v-toolbar-title>

            <v-spacer/>
            <v-toolbar-items>

              <v-btn small flat color="accent" @click="refreshXeroTenantInfo">
                <v-layout column class="align-center">
                  <v-icon>download</v-icon>
                  Refresh
                </v-layout>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
          <template v-if="!hasCurrentCredential || daysUntilXeroCredentialsExpire(company) < 8">
            <v-alert
              :value="!hasCurrentCredential"
              type="error"
            >
              <strong>Requires Authentication</strong>
            </v-alert>
            <v-alert type="warning"
                     :value="hasCurrentCredential && daysUntilXeroCredentialsExpire(company) < 8">
              <strong>Credentials expire in {{ daysUntilXeroCredentialsExpire(company) }} days</strong>
            </v-alert>
            <div class="pa-2">
              <p>For your security, Xero requires that you provide Xero credentials at
                least every 60 days.</p>
              <p>Click below to continue allowing {{ $t('site-title.brief') }} to communicate with your
                Xero tenant.</p>
            </div>
          </template>

          <v-list two-lines>
            <v-subheader>
              <v-icon class="pr-2">settings</v-icon>
              Xero Company Profile
            </v-subheader>

            <v-list-tile>
              <v-list-tile-action>
                <v-icon color="primary">
                  business
                </v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>{{ _.get(xeroDirect, 'companyName') }}</v-list-tile-title>
                <v-list-tile-sub-title>{{ _.get(xeroDirect, 'companyLegalName') }}</v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile>
              <v-list-tile-action>
                <v-icon color="primary">
                  repeat
                </v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title><code>{{ _.get(xeroDirect, 'subscriptionPlan', 'Unknown') }}</code> Subscription Plan</v-list-tile-title>
                <v-list-tile-sub-title>{{ xeroSubscriptionRequiredForBilling }}</v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile v-if="configuredToCollectTax">
              <v-list-tile-action>
                <v-icon color="primary">
                  account_balance
                </v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>Default Tax Codes Applied</v-list-tile-title>
                <v-list-tile-sub-title>to invoice and bill transactions. Can be modified per load if necessary.</v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>

            <v-subheader>
              <v-icon class="pr-2">fingerprint</v-icon>
              Authorization
            </v-subheader>

            <v-list-tile>
              <v-list-tile-action>
                <v-icon color="primary">
                  person_outline
                </v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>
                  {{ _.get(xeroDirect, 'authenticatedBy')}}
                </v-list-tile-title>
                <v-list-tile-sub-title>
                  <span class="font-italic">{{ _.get(xeroDirect, 'authenticated') | formatDate }}</span>
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile>
              <v-list-tile-action>
                <v-icon color="primary">event</v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>{{ _.get(xeroDirect, 'credentialExpires') | formatDay }}</v-list-tile-title>
                <v-list-tile-sub-title>
                  <span class="font-italic">Must re-authorize {{ $t('site-title.brief') }} on or before</span>
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile v-if="hasOfficePermission">
              <v-list-tile-action>
                <v-icon color="primary">autorenew</v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>
                  Authorize {{ $t('site-title.brief') }}
                </v-list-tile-title>
              </v-list-tile-content>

              <v-spacer/>
              <v-xero-direct-connect-btn :company-id="company.id"
                                         :connecting="loadingBtn"
                                         @connecting="loadingBtn = true"
                                         @connected="xeroConnected"

              />
<!--              <v-xero-direct-connect-btn @click="connectTenant"-->
<!--                                         :loading="xdButtonLoading"/>-->
            </v-list-tile>
            <v-list-tile v-if="hasOfficePermission && isConnected">
              <v-list-tile-action>
                <v-icon color="red">clear</v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>
                  Revoke Permission
                </v-list-tile-title>
              </v-list-tile-content>

              <v-spacer/>
              <v-btn flat class="pl-0 pr-0 mr-2" color="red"
                     @click="disconnectTenant">
                <v-icon class="pr-2">cancel</v-icon>
                Revoke
              </v-btn>
            </v-list-tile>
          </v-list>

        </v-tab-item>
        <v-tab-item value="defaults" lazy>
          <v-toolbar flat color="white" class="darken-1">
            <v-xero-icon/>
            <v-toolbar-title>Xero Export Defaults</v-toolbar-title>

            <v-spacer/>
            <v-toolbar-items>
              <v-btn small flat color="accent" @click="showExportDefaultsDialog">
                <v-layout column class="align-center">
                  <v-icon>edit</v-icon>
                  Edit
                </v-layout>
              </v-btn>
              <v-btn small flat color="accent" @click="reloadCompanyXeroData">
                <v-layout column class="align-center">
                  <v-icon>refresh</v-icon>
                  Reload
                </v-layout>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
        </v-tab-item>
      </v-tabs-items>

    </v-card>
    <v-bottom-nav :active.sync="activeTab"
                      :value="true">
          <v-btn flat color="accent" value="connect">
            Connect
            <v-icon :color="xBlue">offline_bolt</v-icon>
          </v-btn>
          <v-btn flat color="accent"
                 value="defaults"
                 :disabled="!isConnected">
            Export Defaults
            <V-icon :color="xBlue">import_export</V-icon>
          </v-btn>
          <v-btn flat color="accent"
                 value="customers"
                 :disabled="!isConnected">
            Customers
            <v-icon :color="xBlue">face</v-icon>
          </v-btn>
          <v-btn flat color="accent"
                 value="qb_vendors"
                 :disabled="!isConnected">
            Vendors
            <v-icon :color="xBlue">share</v-icon>
          </v-btn>
        </v-bottom-nav>
  </v-container>
</template>

<script>
// https://developer.xero.com/documentation/xero-app-store/app-partner-guides/sign-in/
import API from '@/api';
import XeroMixin from '../../../mixins/xeroMixin';
import { accountHasOfficePermission } from '@/store/permissions';
import VXeroDirectConnectBtn from '../../../components/xero/XeroDirectConnectButton.vue';
import VXeroIcon from '../../../components/xero/XeroIcon.vue';

export default {
  name: 'XeroSection',
  components: {
    VXeroIcon,
    VXeroDirectConnectBtn
  },
  mixins: [
    XeroMixin,
  ],
  props: {
    company: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      // activeTab: 'customers',
      activeTab: 'connect',

      customerSearch: null,
      vendorSearch: null,

      showUpdateVendorDispatcher: false,
      vendorDispatcher: null,

      showExportDefaults: false,
      editExportDefaults: null,
      exportDefaultsStep: null,
      refDbKey: null,
      loadingBtn: null,
      loadingTaxCodes: false,

      xdButtonLoading: false,
      xeroConnectStarted: false,
    };
  },
  computed: {
    _() {
      return _;
    },
    xBlue(withHash = true) {
      if (withHash) {
        return '#13B5EA';
      }
      return '13B5EA';
    },
    xeroDirect() {
      const xd = this.xeroDirectFor(this.company);
      return xd;
    },
    configuredToCollectTax() {
      return this.xeroCompanyCollectsTax(this.company);
    },

    isConnected() {
      return this.isXeroConnected(this.company);
    },
    hasCurrentCredential() {
      return this.isXeroCredentialsValid(this.company);
    },
    hasOfficePermission() {
      return this.isLtnAdmin || accountHasOfficePermission(this.userAccountCompany);
    },
  },
  methods: {
    async xeroConnected() {
      this.loadingBtn = false;
      await this.$store.dispatch('companies/getProfileCompany', { id: this.company.id });
      this.refreshCompanyXeroInfo(this.company.id);
      this.showExportDefaultsDialog();
    },
    async refreshXeroTenantInfo() {
      await API.xero.refreshCompanyXeroInfo(this.company.id);
    },
    async requestUpdateCompany() {
      await this.$store.dispatch('companies/getProfileCompany', { id: this.company.id });
      await this.$store.dispatch('companies/getCompany', { id: this.company.id });
      this.loadCompanyXeroInfo(this.company.id);
    },
    async disconnectTenant() {
      // Open oauth page, close it on redirectUri
      this.xdButtonLoading = true;

      const confirmText = `Are you sure you want to disconnect Xero from ${this.$t('site-title.brief')}? This will delete all customer and ${this.$t('terms.vendor')} mappings, as well as default ${this.$t('terms.vendor')} discounts`;
      const confirm = await this.$confirm(confirmText);

      try {
        if (confirm) {
          await API.xero.revokeAuthentication({ companyId: this.company.id });
          this.requestUpdateCompany();
          this.$store.dispatch('applications/setSuccessNotification', {
            message: 'Xero no longer connected... reloading profile',
          });
        }
      } finally {
        this.xdButtonLoading = false;
      }
    },
    reloadCompanyXeroData() {
      this.loadCompanyXeroInfo(this.company.id);
    },
    async showExportDefaultsDialog() {
      console.log('SHOW XERO EXPORT DEFAULTS now');
      this.exportDefaultsStep = 1;

      this.editExportDefaults = {
        invoices: {
          salesItem: this.defaultInvoiceSalesItem.itemId ? this.defaultInvoiceSalesItem : null,
          paymentTerms: this.defaultCustomerInvoiceTerms.id ? this.defaultCustomerInvoiceTerms : null,
          taxCode: this.defaultTaxCodes.sales,
        },
        bills: {
          expenseAccount: this.defaultTransportationFeeExpenseAccount.accountId ? this.defaultTransportationFeeExpenseAccount : null,
          paymentTerms: this.defaultVendorBillTerms.id ? this.defaultVendorBillTerms : null,
          dispatchFeeAccount: this.defaultDispatchFeeIncomeAccount.accountId ? this.defaultDispatchFeeIncomeAccount : null,
          trailerLeaseFeeAccount: this.defaultTrailerLeaseFeeIncomeAccount.accountId ? this.defaultTrailerLeaseFeeIncomeAccount : null,
          taxCode: this.defaultTaxCodes.purchase,
        },
      };

      this.showExportDefaults = true;
    },
    onCompanyUpdate() {
      if (this.xeroConnectStarted && this.isConnected) {
        this.showExportDefaultsDialog();
        this.xeroConnectStarted = false;
      }
    },
  },
}
</script>

<style scoped>
.full-card-desktop {
  height: calc(100vh - 260px);
  overflow: auto;
}

.tall-title {
  height: 34px;
}
</style>

<style>
.xd_tabs .v-tabs__wrapper {
  display: none;
}
</style>



// WEBPACK FOOTER //
// src/pages/Companies/Sections/XeroSection.vue